import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { toast } from 'react-toastify';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import {
  deleteTrainingModuleFile,
  downloadTrainingModuleFile,
  getTrainingModuleFilesList,
  openTrainingModuleFile,
  uploadTrainingModuleFile,
} from 'http/trainingModuleFiles';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig, resolveApiErrorMessage, transformTableData } from 'utils';
import getTrainingModuleFilesTableConfig from './helpers/getTrainingModuleFilesTableConfig';

const ACCEPTED_FILE_FORMAT = '.pdf, .doc, .docx, .ppt, .pptx, .xlsx, .jpeg, .jpg, .png';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  hiddenInput: {
    display: 'none',
  },
}));

const TrainingModuleFilesTable = ({
  trainingModuleId,
  canAddFiles = true,
  canDeleteFiles = true,
  isSystemModule = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const fileInputRef = useRef(null);
  const tableConfig = useMemo(() => getTrainingModuleFilesTableConfig(), []);
  const [files, setFiles] = useState([]);

  const closeModal = useCallback(() => dispatch(modalActions.closeModal()), [dispatch]);

  const [filesState, fetchFiles] = useAsyncFn(async () => {
    return getTrainingModuleFilesList(trainingModuleId, {
      page: 0,
      pageSize: 100,
    });
  }, [trainingModuleId]);

  const refreshFiles = useCallback(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (trainingModuleId) {
      fetchFiles();
    }
  }, [fetchFiles, trainingModuleId]);

  useEffect(() => {
    if (!filesState.value?.error && !filesState.value?.statusCode) {
      setFiles(filesState.value?.items || []);
    }
  }, [filesState.value]);

  const showError = useCallback(
    message => {
      toast.error(
        resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      );
    },
    [t],
  );

  const [uploadState, uploadFile] = useAsyncFn(
    async file => {
      const result = await uploadTrainingModuleFile(trainingModuleId, file);
      if (!result?.error && !result?.statusCode) {
        refreshFiles();
        return result;
      }
      showError(result?.message);
      return result;
    },
    [refreshFiles, showError, trainingModuleId],
  );

  const [deleteState, removeFile] = useAsyncFn(
    async fileId => {
      const result = await deleteTrainingModuleFile(trainingModuleId, fileId);
      if (!result?.error && !result?.statusCode) {
        closeModal();
        refreshFiles();
        return result;
      }
      showError(result?.message);
      return result;
    },
    [closeModal, refreshFiles, showError, trainingModuleId],
  );

  const [actionState, runFileAction] = useAsyncFn(async ({ action, fileId, fileName }) => {
    try {
      if (action === 'view') {
        await openTrainingModuleFile(trainingModuleId, fileId, fileName);
      } else {
        await downloadTrainingModuleFile(trainingModuleId, fileId, fileName);
      }
      return { success: true };
    } catch (error) {
      showError(error?.message);
      return error;
    }
  });

  const handleAddClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = event => {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDeleteFile = useCallback(
    (fileId, fileName) => {
      const [config, element] = getConfirmationModalConfig({
        title: t('trainingModuleFiles.deleteModalTitle'),
        confirmAction: () => removeFile(fileId),
        rejectAction: closeModal,
        message: t('trainingModuleFiles.deleteConfirmationMessage', { fileName }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, removeFile, t],
  );

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'visibility',
        title: t('trainingModuleFiles.actionView'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            runFileAction({ action: 'view', fileId, fileName: file.name });
          }
        },
      },
      {
        type: 'icon',
        icon: 'get_app',
        title: t('trainingModuleFiles.actionDownload'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            runFileAction({ action: 'download', fileId, fileName: file.name });
          }
        },
      },
      {
        type: 'icon',
        icon: 'delete',
        color: 'error',
        title: t('trainingModuleFiles.actionDelete'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            handleDeleteFile(fileId, file.name);
          }
        },
        getHidden: rowData => {
          if (!canDeleteFiles) {
            return true;
          }
          if (!isSystemModule) {
            return false;
          }
          const file = files.find(item => item.id === rowData?.id);
          return file?.source === 'system';
        },
      },
    ],
    [canDeleteFiles, files, handleDeleteFile, isSystemModule, runFileAction, t],
  );

  const [columns, rows] = transformTableData(tableConfig, files);
  const isLoading = filesState.loading || uploadState.loading || deleteState.loading || actionState.loading;

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t('trainingModuleFiles.sectionTitle')}
        </Typography>
        {canAddFiles ? (
          <>
            <Button color="primary" variant="contained" startIcon={<Icon>add</Icon>} onClick={handleAddClick}>
              {t('trainingModuleFiles.actionAdd')}
            </Button>
            <input
              ref={fileInputRef}
              className={classes.hiddenInput}
              type="file"
              accept={ACCEPTED_FILE_FORMAT}
              onChange={handleFileSelected}
            />
          </>
        ) : null}
      </Box>
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={files.length}
          rowsPerPage={files.length || 10}
          actions={tableActions}
        />
      </ModalBusyContainer>
    </Box>
  );
};

TrainingModuleFilesTable.propTypes = {
  trainingModuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canAddFiles: PropTypes.bool,
  canDeleteFiles: PropTypes.bool,
  isSystemModule: PropTypes.bool,
};

export default TrainingModuleFilesTable;
